import React from "react";

// ===================================================================
//  MidasLogo — Gold "M" mark on dark navy circle
//  Sizes: sm (28px), md (36px), lg (48px)
//  Animations:
//    pulse — breathing opacity (thinking/streaming default)
//    fill  — molten gold rising from bottom (clip-path)
//    trace — CNC toolpath stroke draw
//    none  — static solid M
//
//  All animations use SVG SMIL <animate> elements (NOT CSS @keyframes).
//  CSS-based animations failed repeatedly in React/MUI/Emotion context
//  due to style injection conflicts. SMIL is native SVG animation and
//  works reliably in all modern browsers without CSS dependencies.
// ===================================================================

const MIDAS_GOLD = "#E8A817";
const MIDAS_NAVY = "#1B2130";

const M_PATH =
  "M30 84 L30 38 L45 53 L60 38 L75 53 L90 38 L90 84 L78 84 L78 52 L75 56 L60 40 L45 56 L42 52 L42 84 Z";

const SIZE_MAP: Record<MidasLogoSize, number> = {
  sm: 28,
  md: 36,
  lg: 48,
};

type MidasLogoSize = "sm" | "md" | "lg";
type MidasLogoAnimation = "fill" | "pulse" | "trace" | "none";

interface MidasLogoProps {
  size?: MidasLogoSize;
  animated?: MidasLogoAnimation;
  style?: React.CSSProperties;
}

/* ---------- Pulse animation (breathing opacity) ---------- */
const PulseM: React.FC = () => (
  <path d={M_PATH} fill={MIDAS_GOLD}>
    <animate
      attributeName="opacity"
      values="1;0.45;1"
      dur="2s"
      repeatCount="indefinite"
    />
  </path>
);

/* ---------- Fill animation (gold rising from bottom) ---------- */
const FillM: React.FC<{ clipId: string }> = ({ clipId }) => (
  <>
    {/* Ghost M at 15% opacity — always visible */}
    <path d={M_PATH} fill={MIDAS_GOLD} opacity={0.15} />
    {/* Animated clip-path fill — uses SMIL animate on a clipPath rect */}
    <defs>
      <clipPath id={clipId}>
        <rect x="0" y="120" width="120" height="120">
          <animate
            attributeName="y"
            values="120;0;120"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </rect>
      </clipPath>
    </defs>
    <path d={M_PATH} fill={MIDAS_GOLD} clipPath={`url(#${clipId})`} />
  </>
);

/* ---------- Trace animation (stroke draw) ---------- */
const TraceM: React.FC = () => (
  <>
    {/* Ghost fill at 15% opacity */}
    <path d={M_PATH} fill={MIDAS_GOLD} opacity={0.15} />
    {/* Stroke trace animation */}
    <path
      d={M_PATH}
      fill="none"
      stroke={MIDAS_GOLD}
      strokeWidth={2}
      strokeDasharray={220}
      strokeDashoffset={220}
    >
      <animate
        attributeName="stroke-dashoffset"
        values="220;0;220"
        dur="2.5s"
        repeatCount="indefinite"
      />
    </path>
  </>
);

/* ---------- Static M (no animation) ---------- */
const StaticM: React.FC = () => (
  <path d={M_PATH} fill={MIDAS_GOLD} />
);

const MidasLogo: React.FC<MidasLogoProps> = ({
  size = "md",
  animated = "none",
  style,
}) => {
  const px = SIZE_MAP[size];
  const clipId = React.useId().replace(/:/g, "_") + "fill";

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: "block", ...style }}
      aria-hidden="true"
    >
      <circle cx={60} cy={60} r={60} fill={MIDAS_NAVY} />

      {animated === "fill" ? (
        <FillM clipId={clipId} />
      ) : animated === "trace" ? (
        <TraceM />
      ) : animated === "pulse" ? (
        <PulseM />
      ) : (
        <StaticM />
      )}
    </svg>
  );
};

export default React.memo(MidasLogo);
